'use client';

import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ChevronDownIcon, DotIcon } from '@/design-system/components/Icons';
import { Text } from '@/design-system/components/Basic/Typography';

export interface DropdownOption {
  label: string;
  value: string;
  /** Second line, for Label + Content combobox variant */
  content?: string;
  /** Icon element for Icon + Label combobox variant */
  icon?: React.ReactNode;
  /** Show blue dot as active/selected indicator (Icon + Label variant) */
  showActiveIndicator?: boolean;
  /** Option is disabled and not selectable */
  disabled?: boolean;
}

export interface DropdownProps {
  id?: string;
  value: string | null;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  emptyMessage,
  disabled = false,
  error,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Find selected option
  const selectedOption = options.find((opt) => opt.value === value);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen || disabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, disabled]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleSelect = (option: DropdownOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={classNames('relative flex flex-col gap-3', className)}>
      {/* Trigger  */}
      <button
        id={id}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={!!error}
        className={classNames(
          'ds-input-trigger-base flex items-center justify-between gap-ds-2-5',
          { 'ds-input-trigger-focus': isOpen, 'cursor-pointer': !disabled },
        )}
      >
        <Text size="s" className={selectedOption ? 'text-ds-text-1' : 'text-ds-text-3'}>
          {selectedOption?.label || placeholder}
        </Text>
        <ChevronDownIcon
          className={classNames(
            'w-5 h-5 shrink-0 text-ds-icon-3 transition-transform duration-200',
            { 'rotate-180': isOpen },
          )}
        />
      </button>

      {/* Dropdown list – combobox options */}
      {isOpen && (
        <ul
          role="listbox"
          className={classNames(
            'ds-legacy__ul',
            'absolute top-full left-0 right-0 mt-1 z-50',
            'rounded-ds-input border border-ds-input-border bg-ds-input-bg',
            'shadow-ds-select-field-options max-h-60 overflow-auto py-1 flex flex-col gap-0.5',
            '!p-2',
          )}
        >
          {options.length === 0
            ? emptyMessage && (
                <li className="py-2 px-3 text-ds-text-s text-ds-text-3 text-center">
                  {emptyMessage}
                </li>
              )
            : options.map((option) => {
                const isSelected = option.value === value;
                const isDisabled = option.disabled;
                const hasContent = !!option.content;
                const hasIcon = option.icon !== undefined;
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(option)}
                      disabled={isDisabled}
                      className={classNames(
                        'group py-2 px-3 w-full text-left rounded-ds-0 text-ds-text-s transition-colors duration-150 flex items-center gap-2',
                        'rounded-ds-2',
                        isDisabled
                          ? 'bg-transparent text-ds-text-3 cursor-not-allowed opacity-70'
                          : classNames(
                              'cursor-pointer text-ds-text-1',
                              'hover:bg-ds-bg-surface-soft-1',
                              { 'bg-ds-bg-surface-soft-4': isSelected },
                            ),
                      )}
                    >
                      {option.showActiveIndicator && (
                        <DotIcon className="w-4 h-4 fill-ds-icon-2"></DotIcon>
                      )}
                      {hasIcon && (
                        <span
                          className={classNames(
                            'shrink-0 w-5 h-5 flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5',
                            isDisabled
                              ? 'text-ds-icon-4'
                              : 'text-ds-text-3 group-hover:text-ds-text-1',
                            isSelected && !isDisabled && 'text-ds-text-1',
                          )}
                        >
                          {option.icon}
                        </span>
                      )}
                      <span className="flex flex-col gap-0 min-w-0">
                        {hasContent ? (
                          <>
                            <Text size="s" className="text-ds-text-3">
                              {option.label}
                            </Text>
                            <Text size="s" weight="semibold">
                              {option.content}
                            </Text>
                          </>
                        ) : (
                          <Text
                            size="s"
                            className={isDisabled ? 'text-ds-text-3' : 'text-ds-text-1'}
                          >
                            {option.label}
                          </Text>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
        </ul>
      )}

      {/* Error message */}
      {error && (
        <p className="text-ds-caption text-ds-text-feedback-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default Dropdown;
